import React, { useRef, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import { InventoryValueTrend } from "@/gen/inventory/common_pb";

interface InventoryTrendLineChartProps {
  data: InventoryValueTrend[];
}

const LINE_COLOR = "#2563eb"; // blue-600

const chartConfig = {
  inventory_value: {
    label: "Inventory Value",
    color: LINE_COLOR,
  },
};

function formatMonthYear(month: string, year: string) {
  // month: '03', year: '2023' or month: 'March', year: '2023'
  let monthStr = month;
  if (/^\d+$/.test(month)) {
    // If month is a number string, convert to short month name
    const monthNum = parseInt(month, 10);
    monthStr = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][monthNum - 1] || month;
  } else if (month.length > 3) {
    monthStr = month.slice(0, 3);
  }
  return `${monthStr} ${year}`;
}

const InventoryTrendLineChart: React.FC<InventoryTrendLineChartProps> = ({ data }) => {
  const isScrollable = data.length > 6;
  const chartData = data.map((d) => ({
    ...d,
    label: formatMonthYear(d.date?.toDate().getMonth().toString()!, d.date?.toDate().getFullYear().toString()!),
  }));

  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(480);

  useEffect(() => {
    if (scrollRef.current && !isScrollable) {
      setContainerWidth(scrollRef.current.offsetWidth);
    }
  }, [isScrollable]);

  useEffect(() => {
    if (isScrollable && scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [isScrollable, data.length]);

  const chartWidth = isScrollable ? data.length * 80 : containerWidth;

  return (
    <Card className={`bg-[#FCFCFC] flex flex-col h-full`}>
      <div className="m-4 flex items-center text-gray-600">
        <TrendingUp className="w-4 h-4" />
        <p className="text-sm ml-1">Inventory Trend</p>
      </div>
      <hr className="my-0" />
      <CardHeader>
        <CardTitle>Inventory Value Trend</CardTitle>
        <CardDescription>Monthly inventory value</CardDescription>
      </CardHeader>
      <CardContent className="max-h-[320px]">
        <ChartContainer config={chartConfig} className="max-h-[250px] w-full" >
          <div
            ref={scrollRef}
            className={"w-full " + (isScrollable ? "overflow-x-auto" : "")}
            style={{ height: 250 }}
          >
            <LineChart
              width={chartWidth}
              height={220}
              data={chartData}
              margin={{ left: 12, right: 12 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                // Show full label (e.g., 'Mar 2023')
              />
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Line
                dataKey="value"
                type="linear"
                stroke={LINE_COLOR}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </div>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="flex gap-2 font-medium leading-none">
          Trending up by 5.2% this month <TrendingUp className="h-4 w-4" />
        </div>
        <div className="leading-none text-muted-foreground">
          Showing inventory value trend
        </div>
      </CardFooter>
    </Card>
  );
};

export default InventoryTrendLineChart; 